'use client';

import React, { useEffect } from 'react';
import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import TilesArrayRenderer from '@/ui/components/tileRenderers/PageGridRenderer/tiles-array-renderer';
import { ClassicPdpProductProvider } from '@/ui/providers/classic-pdp-product-provider';
import { ProductDetailDataProvider } from '@/ui/providers/product-detail-data-provider';
import { useSubnavData } from '@/ui/providers/subnavDataProvider';
import { readClassicPdpProducts } from '@/ui/components/tiles/classic-product-detail/classic-pdp-entry';
import { CrystallizeProductData } from '@/ui/components/pages/ProductDetail/types/CrystallizeProductData';

export interface ProductLandingPageContentProps {
  data: CrystallizeProductData;
}

export function ProductLandingPageContent({ data }: Readonly<ProductLandingPageContentProps>) {
  const landingPageTiles = componentByTypename(
    data.landingPageTiles?.content,
    'ItemRelationsContent',
  )?.items;
  const { setSubnavData } = useSubnavData();

  const subnavItem = componentByTypename(data?.subnav?.content, 'ItemRelationsContent')?.items?.[0];
  const subnavData = componentByTypename(subnavItem, 'Document');

  // using ts-ignore here because the technicalData is only on hardware products
  // and the type above is union for all products and at this level we have no way to verify
  // if the product is a hardware product or not, it's anyways escaped using ? in case it's
  // not there so it's safe to ignore
  const technicalDataTile = componentByTypename(
    // @ts-ignore
    data.technicalData?.content,
    'ItemRelationsContent',
  )?.items;

  useEffect(() => {
    setSubnavData(subnavData);

    return () => {
      setSubnavData(null);
    };
  }, [subnavData, setSubnavData]);

  return (
    // Tiles placed by editors can render product content without the page
    // passing it through every tile renderer.
    <ProductDetailDataProvider data={data}>
      {/* A classic product detail tile placed here reads its own product
      relation, which the server resolved onto this payload. */}
      <ClassicPdpProductProvider entries={readClassicPdpProducts(data)}>
        <div>
          {landingPageTiles && (
            <TilesArrayRenderer tiles={landingPageTiles as TileFragmentFragment[]} />
          )}
          {technicalDataTile && (
            <TilesArrayRenderer tiles={technicalDataTile as TileFragmentFragment[]} />
          )}
        </div>
      </ClassicPdpProductProvider>
    </ProductDetailDataProvider>
  );
}
